import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { Button, Space } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from '@ant-design/icons';

import { pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

interface CustomPdfViewerProps {
  fileUrl: string;
  height?: string | number;
  width?: string | number;
}

const CustomPdfViewer: React.FC<CustomPdfViewerProps> = ({
  fileUrl,
  height = '100%',
  width = '100%',
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.8);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const resetZoom = () => {
    setScale(0.8);
  };

  const rotateLeft = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
  };

  const rotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const scrollToPageTop = (pageNum: number) => {
    const pageElement = document.querySelector(
      `[data-page-number="${pageNum}"]`
    ) as HTMLElement;
    if (pageElement && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const pageTop = pageElement.offsetTop;
      container.scrollTo({
        top: pageTop,
        behavior: 'smooth',
      });
    }
  };

  const goToPrevPage = () => {
    const newPage = Math.max(pageNumber - 1, 1);
    scrollToPageTop(newPage);
  };

  const goToNextPage = () => {
    const newPage = Math.min(pageNumber + 1, numPages || 1);
    scrollToPageTop(newPage);
  };

  // Drag to scroll functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      scrollLeft: scrollContainerRef.current.scrollLeft,
      scrollTop: scrollContainerRef.current.scrollTop,
    });

    // Change cursor to grabbing
    document.body.style.cursor = 'grabbing';
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    scrollContainerRef.current.scrollLeft = dragStart.scrollLeft - deltaX;
    scrollContainerRef.current.scrollTop = dragStart.scrollTop - deltaY;

    e.preventDefault();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = 'default';
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    document.body.style.cursor = 'default';
  };

  // Update page number based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || !numPages) return;

      const container = scrollContainerRef.current;
      const containerTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const scrollPosition = containerTop + containerHeight / 2;

      // If we're at the very top, set to page 1
      if (containerTop <= 10) {
        setPageNumber(1);
        return;
      }

      // Find which page is currently in the center of the viewport
      let currentPage = 1; // Default to page 1
      for (let i = 1; i <= numPages; i++) {
        const pageElement = document.querySelector(
          `[data-page-number="${i}"]`
        ) as HTMLElement;
        if (pageElement) {
          const pageTop = pageElement.offsetTop;
          const pageHeight = pageElement.clientHeight;

          if (
            scrollPosition >= pageTop &&
            scrollPosition < pageTop + pageHeight
          ) {
            currentPage = i;
            break;
          }
          // If we're past this page, update currentPage (for cases between pages)
          if (scrollPosition > pageTop) {
            currentPage = i;
          }
        }
      }

      setPageNumber(currentPage);
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [numPages]);

  return (
    <div
      style={{
        height,
        width,
        border: '1px solid #e8e8e8',
        borderRadius: '6px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Custom Toolbar */}
      <div
        style={{
          borderBottom: '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Space>
          <Button
            size="small"
            icon={<LeftOutlined />}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            Previous
          </Button>
          <Button
            size="small"
            icon={<RightOutlined />}
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 1)}
          >
            Next
          </Button>
          <span style={{ fontSize: '14px' }}>
            {numPages === 1
              ? 'Page 1'
              : `Page ${pageNumber} of ${numPages || '--'}`}
          </span>
        </Space>
        <Space>
          <Button size="small" icon={<ZoomOutOutlined />} onClick={zoomOut} />
          <span
            style={{ fontSize: '12px', minWidth: '40px', textAlign: 'center' }}
          >
            {Math.round(scale * 100)}%
          </span>
          <Button size="small" icon={<ZoomInOutlined />} onClick={zoomIn} />
          <Button size="small" onClick={resetZoom}>
            Reset
          </Button>
          <Button
            size="small"
            icon={<RotateLeftOutlined />}
            onClick={rotateLeft}
          />
          <Button
            size="small"
            icon={<RotateRightOutlined />}
            onClick={rotateRight}
          />
        </Space>
      </div>

      {/* PDF Viewer */}
      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div>Loading PDF...</div>}
          error={<div>Error loading PDF</div>}
        >
          {numPages &&
            Array.from(new Array(numPages), (el, index) => (
              <div
                key={`page_${index + 1}`}
                style={{ marginBottom: '10px' }}
                data-page-number={index + 1}
              >
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            ))}
        </Document>
      </div>
    </div>
  );
};

export default CustomPdfViewer;
